import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Joi from "joi-browser";
import { toast } from "react-toastify";

import Lottie from "lottie-react";
import moment from "moment";
import Input from "./common/Input";
import Select from "./common/Select";
import Checkbox from "./common/Checkbox";
import TextArea from "./common/TextArea";
import BackIcon from "./common/BackIcon";
import * as contactLoading from "../animations/profile.json";
import correspondenceOptionsets from "../options/correspondence.json";
import { getSoarCorrespondence, searchContact } from "./services/soar";
import {
  formatDateTime,
  getFieldText,
  getFieldName,
  getFieldFormattedValue,
  updateEntity,
  createEntity,
} from "../scripts/helperScripts";

function SOARCorrespondenceForm(props) {
  const location = useLocation();
  const fosterHome = location.state?.fosterHome;
  const addresses = location.state?.addresses;
  const contact = location.state?.contact;
  const correspondence = location.state?.correspondence;
  const [correspondenceData, setCorrespondenceData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [contactOptions, setContactOptions] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [errors, setErrors] = useState({});
  const isMountedRef = useRef(true);
  const navigate = useNavigate();
  const safyInfo = JSON.parse(localStorage.getItem("safyInfo"));

  const joiSchema = {
    sor_soarcorrespondanceid: Joi.string().allow(null),
    sor_dateandstarttime: Joi.date()
      .required()
      .max("now")
      .min(new Date("1/1/1900"))
      .label("Date and Start Time")
      .error((errors) => {
        errors.forEach((error) => {
          switch (error.type) {
            case "date.max":
              error.message = `Date and Start Time cannot be future dated`;
              break;
            case "date.min":
              error.message = `Date and Start Time cannot be before 1/1/1900`;
              break;
            default:
              return error.message;
          }
        });
        return errors;
      }),
    sor_dateandendtime: Joi.any(),
    sor_minutes: Joi.number().required().label("Minutes"),
    sor_units: Joi.any().label("Units"),
    sor_deliverymethod: Joi.string().required().label("Delivery Method"),
    sor_placeofservice: Joi.string().required().label("Place of Service"),
    sor_servicestatus: Joi.string().required().label("Service Status"),
    sor_servicerecipients: Joi.string().required().label("Service Recipients"),
    sor_pssrs: Joi.boolean(),
    sor_ctf: Joi.boolean(),
    sor_progressnote: Joi.string().required().label("Progress Note"),
    sor_esignature: Joi.boolean().label("E Signature"),
    sor_esignuser: Joi.string().allow(null),
    sor_esigndate: Joi.string().allow(null),
    sor_name: Joi.string().allow(null),
    sor_contact: Joi.string().allow(null),
    contactName: Joi.string().allow(null),
    sor_fosterhome: Joi.string().allow(null),
    fosterhomeName: Joi.string().allow(null),
    sor_lockform: Joi.string().allow(null),
  };

  //Route Access Protection
  useEffect(() => {
    if (safyInfo) {
      const modules = safyInfo.user.modules;

      if (
        modules.includes("My Caseload") === false &&
        modules.includes("Team Caseload") === false &&
        modules.includes("Foster Homes") === false
      ) {
        navigate("/");
      }
    } else navigate("/");
  });

  useEffect(() => {
    if (correspondence !== undefined) {
      getSoarCorrespondence(
        correspondence.correspondence.sor_soarcorrespondanceid
      ).then((response) => {
        if (isMountedRef.current) {
          if (response) {
            console.log("Correspondence:", response);
            const cor = response;
            console.log("Cor", cor);
            const corObj = {
              sor_soarcorrespondanceid: cor[0]["sor_soarcorrespondanceid"]
                ? cor[0]["sor_soarcorrespondanceid"]
                : null,
              sor_name: cor[0]["sor_name"],
              sor_contact: getFieldText(cor[0]["sor_contact"]),
              contactName: getFieldName(cor[0]["sor_contact"]),
              sor_fosterhome: getFieldText(cor[0]["sor_fosterhome"]),
              fosterhomeName: getFieldName(cor[0]["sor_fosterhome"]),
              sor_units: cor[0]["sor_units"]
                ? Number(getFieldText(cor[0]["sor_units"])).toFixed(2)
                : getFieldText(cor[0]["sor_units"]),
              sor_dateandstarttime: getFieldText(
                cor[0]["sor_dateandstarttime"]
              ),
              sor_dateandendtime: getFieldText(cor[0]["sor_dateandendtime"]),
              sor_minutes: getFieldText(cor[0]["sor_minutes"]),
              sor_deliverymethod: getFieldFormattedValue(
                cor[0]["sor_deliverymethod"]
              ),
              sor_servicestatus: getFieldFormattedValue(
                cor[0]["sor_servicestatus"]
              ),
              sor_servicerecipients: getFieldFormattedValue(
                cor[0]["sor_servicerecipients"]
              ),
              sor_placeofservice: getFieldFormattedValue(
                cor[0]["sor_placeofservice"]
              ),
              sor_pssrs:
                getFieldName(cor[0]["sor_pssrs"]) === "Yes" ? true : false,
              sor_ctf: getFieldName(cor[0]["sor_ctf"]) === "Yes" ? true : false,
              sor_progressnote: cor[0]["sor_progressnote"],
              sor_lockform: getFieldFormattedValue(cor[0]["sor_lockform"]),
              sor_esignature:
                getFieldName(cor[0]["sor_esignature"]) === "Yes" ? true : false,
              sor_esignuser: cor[0]["sor_esignuser"] || null,
              sor_esigndate: cor[0]["sor_esigndate"] || null,
            };
            setCorrespondenceData(corObj);

            if (cor[0]["sor_contact"]) {
              const option = {
                _id: getFieldText(cor[0]["sor_contact"]),
                name: getFieldName(cor[0]["sor_contact"]),
              };
              setContactOptions([option]);
              setContactsLoaded(true);
            }
          }

          setLoaded(true);
        }
      });
    } else {
      const corObj = {
        sor_soarcorrespondanceid: null,
        sor_name: null,
        sor_contact: contact !== undefined ? contact.contactid : null,
        contactName: contact !== undefined ? contact.fullname : null,
        sor_fosterhome:
          fosterHome !== undefined ? fosterHome.ccx_fostercarehomeid : null,
        fosterhomeName: fosterHome !== undefined ? fosterHome.ccx_name : null,
        sor_units: null,
        sor_dateandstarttime: null,
        sor_dateandendtime: null,
        sor_minutes: null,
        sor_deliverymethod: null,
        sor_servicestatus: null,
        sor_servicerecipients: null,
        sor_placeofservice: null,
        sor_pssrs: false,
        sor_ctf: false,
        sor_progressnote: null,
        sor_lockform: null,
        sor_esignature: false,
        sor_esignuser: null,
        sor_esigndate: null,
      };
      setCorrespondenceData(corObj);
      setLoaded(true);
    }
  }, [correspondence, contact, fosterHome]);

  function CalculateMinutes() {
    setCorrespondenceData((cd) => {
      const obj = { ...cd };
      if (obj.sor_dateandstarttime && obj.sor_minutes) {
        console.log("start and minutes not null");
        const starttime = new Date(obj.sor_dateandstarttime);
        var newTime = moment(starttime)
          .add(obj.sor_minuteS, "m")
          .format(moment.HTML5_FMT.DATETIME_LOCAL);
        obj.sor_dateandendtime = newTime;
        obj.sor_units = obj.sor_minutes / 15;
      } else if (obj.sor_dateandstarttime && !obj.sor_minutes) {
        obj.sor_dateandendtime = obj.sor_dateandstarttime;
      }

      return obj;
    });
  }

  const handleChange = ({ currentTarget: input }) => {
    console.log("input: ", input, input.value);
    console.log("value: ", input);
    const data = { ...correspondenceData };
    data[input.name] = input.value;
    setCorrespondenceData(data);

    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
      if (
        input.name === "sor_dateandstarttime" ||
        input.name === "sor_minutes"
      ) {
        CalculateMinutes();
      }
    }

    setErrors(errs);
  };

  const handleChecked = ({ currentTarget: input }) => {
    console.log("value: ", input.checked);
    input.value = input.checked;
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
      const data = correspondenceData;
      if (input.name === "sor_pssrs") {
        data.sor_ctf = false;
      } else if (input.name === "sor_ctf") {
        data.sor_pssrs = false;
      }
      data[input.name] = input.checked;
      setCorrespondenceData(data);
    }

    setErrors(errs);
  };

  const handleESignatureChecked = ({ currentTarget: input }) => {
    console.log("value: ", input.checked);
    input.value = input.checked;
    const errs = { ...errors };
    const errorMessage = validateProperty(input);
    if (errorMessage) {
      errs[input.name] = errorMessage;
      console.log("Errors:", JSON.stringify(errs));
    } else {
      delete errs[input.name];
      const data = correspondenceData;
      data[input.name] = input.checked;
      data["sor_esigndate"] = {
        "@date": new Date().toString(),
        "#text": new Date().toString(),
      };
      data["sor_esignuser"] = {
        "@name": safyInfo.user.displayName,
        "#text": safyInfo.user.safyInfo.SOARUserId,
      };

      setCorrespondenceData(data);
    }

    setErrors(errs);
    if (input.checked) {
      handleSave(safyInfo.user.domainName, props);
    }
  };

  const validate = () => {
    const options = { abortEarly: false };
    const { error } = Joi.validate(correspondenceData, joiSchema, options);

    let validationErrors = {};
    if (error)
      for (let item of error.details)
        validationErrors[item.path[0]] = item.message;

    if (Object.keys(validationErrors).length === 0) return null;

    setErrors(validationErrors);
    return validationErrors;
  };

  const validateProperty = ({ name, value }) => {
    console.log("Validate: ", name, value);
    const obj = { [name]: value };
    const schema = { [name]: joiSchema[name] };
    const { error } = Joi.validate(obj, schema, { convert: true });
    return error ? error.details[0].message : null;
  };

  const handleSave = () => {
    const name =
      correspondenceData.sor_name !== undefined
        ? correspondenceData.sor_name
        : contact !== undefined
        ? encodeURIComponent(contact.fullname) +
          "-" +
          new Date(correspondenceData.sor_dateandstarttime).toLocaleString()
        : encodeURIComponent(fosterHome.ccx_name) +
          "-" +
          new Date(correspondenceData.sor_dateandstarttime).toLocaleString();

    const obj = {
      sor_name: {
        type: "string",
        value: name,
      },
      sor_contact: {
        type: "entity",
        value: correspondenceData.sor_contact
          ? {
              entityName: "contact",
              entityId: correspondenceData.sor_contact,
            }
          : "",
      },
      sor_fosterhome: {
        type: "entity",
        value: correspondenceData.sor_fosterhome
          ? {
              entityName: "ccx_fostercarehome",
              entityId: correspondenceData.sor_fosterhome,
            }
          : "",
      },
      sor_units: {
        type: "decimal",
        value: correspondenceData.sor_units,
      },
      sor_dateandstarttime: {
        type: "datetime",
        value: correspondenceData.sor_dateandstarttime
          ? new Date(correspondenceData.sor_dateandstarttime).toLocaleString()
          : null,
      },
      sor_dateandendtime: {
        type: "datetime",
        value: correspondenceData.sor_dateandendtime
          ? new Date(correspondenceData.sor_dateandendtime).toLocaleString()
          : null,
      },
      sor_minutes: {
        type: "number",
        value: correspondenceData.sor_minutes,
      },
      sor_deliverymethod: {
        type: "optionset",
        value: correspondenceData.sor_deliverymethod
          ? correspondenceData.sor_deliverymethod
          : null,
      },
      sor_servicestatus: {
        type: "optionset",
        value: correspondenceData.sor_servicestatus
          ? correspondenceData.sor_servicestatus
          : null,
      },
      sor_servicerecipients: {
        type: "optionset",
        value: correspondenceData.sor_servicerecipients
          ? correspondenceData.sor_servicerecipients
          : null,
      },
      sor_placeofservice: {
        type: "optionset",
        value: correspondenceData.sor_placeofservice
          ? correspondenceData.sor_placeofservice
          : null,
      },
      sor_pssrs: {
        type: "bool",
        value: correspondenceData.sor_pssrs,
      },
      sor_ctf: {
        type: "bool",
        value: correspondenceData.sor_ctf,
      },
      sor_progressnote: {
        type: "string",
        value: correspondenceData.sor_progressnote
          ? correspondenceData.sor_progressnote
          : null,
      },
      sor_lockform: {
        type: "bool",
        value: correspondenceData.sor_esignature,
      },
      sor_esignature: {
        type: "bool",
        value: correspondenceData.sor_esignature,
      },
      sor_esigndate: {
        type: "datetime",
        value: correspondenceData.sor_esigndate
          ? new Date(
              getFieldText(correspondenceData.sor_esigndate)
            ).toLocaleString()
          : null,
      },
      sor_esignuser: {
        type: "entity",
        value: correspondenceData.sor_esignuser
          ? {
              entityName: "systemuser",
              entityId: getFieldText(correspondenceData.sor_esignuser),
            }
          : "",
      },
    };

    if (correspondenceData.sor_soarcorrespondanceid !== null) {
      const toastId = toast.loading("Correspondence is being updated...", {
        type: toast.TYPE.INFO,
      });
      updateEntity(
        "sor_soarcorrespondance",
        correspondenceData.sor_soarcorrespondanceid,
        JSON.stringify(obj),
        safyInfo
      )
        .then(() => {
          toast.update(toastId, {
            render: "Correspondence has been updated.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });

          if (fosterHome && fosterHome.ccx_fostercarehomeid) {
            navigate(`/fosterhomes/${fosterHome.ccx_fostercarehomeid}`, {
              state: {
                tab: "correspondence",
                home: fosterHome,
                addresses: addresses,
              },
            });
          }
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while updating the Correspondence.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    } else {
      const toastId = toast.loading("Correspondence is being creating...", {
        type: toast.TYPE.INFO,
      });
      createEntity(
        "sor_soarcorrespondance",
        JSON.stringify(obj),
        safyInfo,
        contact !== undefined
          ? getFieldName(contact.owningbusinessunit).split(" of ")[1]
          : getFieldName(fosterHome.owningbusinessunit).split(" of ")[1]
      )
        .then(() => {
          toast.update(toastId, {
            render: "Correspondence has been created.",
            type: toast.TYPE.SUCCESS,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });

          if (fosterHome && fosterHome.ccx_fostercarehomeid) {
            navigate(`/fosterhomes/${fosterHome.ccx_fostercarehomeid}`, {
              state: {
                tab: "correspondence",
                home: fosterHome,
                addresses: addresses,
              },
            });
          }
        })
        .catch(() => {
          toast.update(toastId, {
            render: "An error occurred while creating the Correspondence.",
            type: toast.TYPE.ERROR,
            isLoading: false,
            autoClose: 5000,
            closeOnClick: true,
          });
        });
    }
  };

  const handleSearchContacts = async () => {
    setContactsLoaded(false);
    const response = await searchContact(
      searchQuery,
      safyInfo.user.safyInfo.BusinessUnitIdName
    );

    if (!isMountedRef.current) {
      return;
    }

    if (!response) {
      setContactsLoaded(true);
      return;
    }

    const options = response.map((contact) => ({
      _id: contact.contactid,
      name: contact.fullname,
    }));
    setContactOptions(options);
    setContactsLoaded(true);
  };

  return (
    <>
      <div className="d-flex">
        <BackIcon />
        <h3>SOAR Correspondence</h3>
      </div>
      <div>
        {!loaded ? (
          <>
            <div className="d-flex justify-content-center align-items-center">
              <h1>LOADING Correspondence</h1>
              <Lottie
                height={120}
                width={120}
                loop={true}
                autoplay={true}
                animationData={contactLoading.default}
                rendererSettings={{
                  preserveAspectRatio: "xMidYMid slice",
                }}
              />
            </div>
          </>
        ) : (
          <>
            <div className="mb-4 p-4 bg-safy1-1" style={{ color: "white" }}>
              {correspondenceData.sor_name ? (
                <div className="h5">{correspondenceData.sor_name}</div>
              ) : correspondenceData.contactName ? (
                <div className="h4 mt-4">{correspondenceData.contactName}</div>
              ) : (
                correspondenceData.fosterhomeName && (
                  <div className="h4 mt-4">
                    {correspondenceData.fosterhomeName}
                  </div>
                )
              )}
            </div>
            <div className="row ">
              <div className="col-md-4">
                <Input
                  className="p-2"
                  name="sor_dateandstarttime"
                  type="datetime-local"
                  label="Date and Start Time"
                  value={
                    correspondenceData.sor_dateandstarttime
                      ? formatDateTime(correspondenceData.sor_dateandstarttime)
                      : ""
                  }
                  onChange={handleChange}
                  error={errors.sor_dateandstarttime}
                  max={moment().format("yyyy-MM-DDThh:mm")}
                  disabled={correspondenceData.sor_esignature}
                />
              </div>
              <div className="col-md-2">
                <Input
                  className="p-2"
                  name="sor_minutes"
                  label="Minutes"
                  value={
                    correspondenceData.sor_minutes
                      ? correspondenceData.sor_minutes
                      : null
                  }
                  onChange={handleChange}
                  error={errors.sor_minutes}
                  disabled={correspondenceData.sor_esignature}
                />
              </div>
              <div className="col-md-4">
                <Input
                  className="p-2"
                  name="sor_dateandendtime"
                  type="datetime-local"
                  label="Date and End Time"
                  value={
                    correspondenceData.sor_dateandendtime
                      ? formatDateTime(correspondenceData.sor_dateandendtime)
                      : null
                  }
                  onChange={handleChange}
                  error={errors.sor_dateandendtime}
                  disabled={true}
                />
              </div>
              <div className="col-md-2">
                <Input
                  className="p-2"
                  name="sor_units"
                  label="Units"
                  value={
                    correspondenceData.sor_units
                      ? correspondenceData.sor_units
                      : null
                  }
                  disabled={true}
                  onChange={handleChange}
                  error={errors.sor_units}
                />
              </div>
            </div>
            <div className="row row-cols-1 row-cols-sm-1 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 mt-4">
              <Select
                className="col p-2"
                name="sor_deliverymethod"
                label="Delivery Method"
                options={correspondenceOptionsets.sor_deliverymethod}
                value={
                  correspondenceData.sor_deliverymethod
                    ? correspondenceData.sor_deliverymethod
                    : ""
                }
                onChange={handleChange}
                error={errors.sor_deliverymethod}
                disabled={correspondenceData.sor_esignature}
              />
              <Select
                className="col p-2"
                name="sor_placeofservice"
                label="Place of Service"
                options={correspondenceOptionsets.sor_placeofservice}
                value={
                  correspondenceData.sor_placeofservice
                    ? correspondenceData.sor_placeofservice
                    : ""
                }
                onChange={handleChange}
                error={errors.sor_placeofservice}
                disabled={correspondenceData.sor_esignature}
              />
              <Select
                className="col p-2"
                name="sor_servicestatus"
                label="Service Status"
                options={correspondenceOptionsets.sor_servicestatus}
                value={
                  correspondenceData.sor_servicestatus
                    ? correspondenceData.sor_servicestatus
                    : ""
                }
                onChange={handleChange}
                error={errors.sor_servicestatus}
                disabled={correspondenceData.sor_esignature}
              />
            </div>
            {(fosterHome !== undefined &&
              getFieldName(fosterHome.owningbusinessunit) ===
                "SAFY of Nevada") ||
            (contact !== undefined &&
              getFieldName(contact.owningbusinessunit) === "SAFY of Nevada") ? (
              <div className="clearfix">
                <div className="form-check form-switch ">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="sor_pssrs"
                    id="sor_pssrs"
                    checked={correspondenceData.sor_pssrs}
                    onChange={handleChecked}
                    error={errors.sor_pssrs}
                    disabled={correspondenceData.sor_esignature}
                  />
                  <label className="form-check-label" htmlFor="sor_pssrs">
                    PSSF
                  </label>
                </div>
                <div className="form-check form-switch ">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    name="sor_ctf"
                    id="sor_ctf"
                    checked={correspondenceData.sor_ctf}
                    onChange={handleChecked}
                    error={errors.sor_ctf}
                    disabled={correspondenceData.sor_esignature}
                  />
                  <label className="form-check-label" htmlFor="sor_ctf">
                    CTF
                  </label>
                </div>
              </div>
            ) : null}
            <div className="row mt-4">
              <div className="col-4">
                <Select
                  name="sor_servicerecipients"
                  label="Service Recipients"
                  options={correspondenceOptionsets.sor_servicerecipients}
                  value={
                    correspondenceData.sor_servicerecipients
                      ? correspondenceData.sor_servicerecipients
                      : ""
                  }
                  onChange={handleChange}
                  error={errors.sor_servicerecipients}
                  disabled={correspondenceData.sor_esignature}
                />
              </div>
              {fosterHome !== undefined ? (
                <>
                  <div className="col-3">
                    <Input
                      name="search"
                      label="Search for Contact"
                      placeholder="Search for Contact..."
                      onChange={(e) => setSearchQuery(e.currentTarget.value)}
                    />
                  </div>
                  <div className="col-1">
                    <button
                      className="btn btn-primary"
                      style={{ marginTop: "30px" }}
                      onClick={handleSearchContacts}
                    >
                      Search
                    </button>
                  </div>
                  <div className="col-4">
                    <Select
                      name="sor_contact"
                      label="Contact"
                      options={contactOptions}
                      value={correspondenceData.sor_contact}
                      onChange={handleChange}
                      error={errors.sor_contact}
                      disabled={
                        !contactsLoaded || correspondenceData.sor_esignature
                      }
                    />
                  </div>
                </>
              ) : null}
            </div>
            <div>
              <TextArea
                name="sor_progressnote"
                type="textarea"
                label="Note"
                onChange={handleChange}
                value={correspondenceData.sor_progressnote}
                error={errors.sor_progressnote}
                rows="8"
                disabled={correspondenceData.sor_esignature}
              />
            </div>
            <div className="row row-cols-1 row-cols-sm-1 row-cols-md-3 row-cols-lg-3 row-cols-xl-3 mt-4">
              <div className="col-md-2 p-2 ps-4">
                <Checkbox
                  name="sor_esignature"
                  label="E Signature"
                  checked={correspondenceData.sor_esignature === true}
                  onChange={handleESignatureChecked}
                  disabled={correspondenceData.sor_esignature === true}
                />
              </div>
              <div className="col-md-2 p-2">
                {getFieldName(correspondenceData.sor_esignuser)}
              </div>
              <div className="col-md-2 p-2">
                {correspondenceData.sor_esigndate &&
                  moment(getFieldText(correspondenceData.sor_esigndate)).format(
                    "LLL"
                  )}
              </div>
            </div>
            <button
              className={
                Object.keys(errors).length === 0
                  ? "btn btn-primary m-2"
                  : "btn btn-secondary m-2"
              }
              onClick={() => {
                if (!validate()) handleSave(safyInfo.user.domainName, props);
              }}
              disabled={
                Object.keys(errors).length !== 0 &&
                correspondenceData.sor_esignature === true
              }
            >
              Submit
            </button>
          </>
        )}
      </div>
    </>
  );
}

export default SOARCorrespondenceForm;
